import { useRef, useState } from 'react';

interface TreemapBlock {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TreemapProps {
  data: TreemapBlock[];
  width?: number;
  height?: number;
  colorPalette?: string[];
  onBlockClick?: (block: TreemapBlock, color: string) => void;
}

const DEFAULT_COLOR_PALETTE = [
  '#71685F', '#33485B', '#9F6460', '#A7674D',
  '#8C7F75', '#688096', '#D4C4C4', '#CE9A85'
];

const PADDING = 2; // Inner padding for rectangles

export function Treemap({ 
  data, 
  width = 600, 
  height = 400,
  colorPalette = DEFAULT_COLOR_PALETTE,
  onBlockClick 
}: TreemapProps) {
  const [hoveredBlock, setHoveredBlock] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<SVGRectElement>, block: TreemapBlock) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    
    // Calculate initial position
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    
    // Tooltip dimensions (approximate)
    const tooltipWidth = 200;
    const tooltipHeight = 60;
    const offset = 10;
    
    // Adjust position if tooltip would go outside container bounds
    if (x + tooltipWidth + offset > width) {
      x = x - tooltipWidth - offset; // Position to the left of cursor
    } else {
      x = x + offset; // Default position to the right
    }
    
    if (y + tooltipHeight + offset > height) {
      y = y - tooltipHeight - offset; // Position above cursor
    } else {
      y = y + offset; // Default position below
    }
    
    // Ensure tooltip doesn't go beyond left/top edges
    x = Math.max(0, x);
    y = Math.max(0, y);
    
    setTooltipPosition({ x, y });
    setHoveredBlock(block.label);
  };

  const handleMouseLeave = () => {
    setHoveredBlock(null);
    setTooltipPosition(null);
  };

  // Function to determine if text should be white or black based on background color
  const getContrastColor = (hexColor: string): string => {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  return (
    <div className="flex flex-col" style={{ overflow: 'visible' }}>
      <div 
        ref={containerRef}
        className="relative"
        style={{ 
          width: `${width}px`, 
          height: `${height}px`,
          borderRadius: '0px',
          overflow: 'visible'
        }}
      >
        <svg width={width} height={height} className="absolute inset-0">
          {data.map((block, index) => {
            const color = colorPalette[index % colorPalette.length];
            
            // Calculate base dimensions
            const baseX = block.x * width;
            const baseY = block.y * height;
            const baseWidth = block.width * width;
            const baseHeight = block.height * height;
            
            // Apply padding
            const rectX = baseX + PADDING;
            const rectY = baseY + PADDING;
            const rectWidth = baseWidth - (PADDING * 2);
            const rectHeight = baseHeight - (PADDING * 2);
            
            const textColor = getContrastColor(color);
            
            // Dynamic font size based on rectangle size
            let fontSize = 10;
            let showLabel = true;
            let truncateLength = 15;
            
            if (rectWidth < 30 || rectHeight < 15) {
              showLabel = false; // Too small for any text
            } else if (rectWidth < 50 || rectHeight < 25) {
              fontSize = 8; // Smaller font for smaller rectangles
              truncateLength = 8;
            } else if (rectWidth < 80 || rectHeight < 35) {
              fontSize = 9;
              truncateLength = 12;
            }
            
            return (
              <g key={`${block.label}-${index}`}>
                <rect
                  x={rectX}
                  y={rectY}
                  width={rectWidth}
                  height={rectHeight}
                  fill={color}
                  className="cursor-pointer transition-all"
                  style={{ 
                    opacity: hoveredBlock && hoveredBlock !== block.label ? 0.6 : 1 
                  }}
                  onMouseMove={(e) => handleMouseMove(e, block)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => onBlockClick?.(block, color)}
                />
                {showLabel && (
                  <text
                    x={rectX + rectWidth / 2}
                    y={rectY + rectHeight / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={textColor}
                    fontSize={fontSize}
                    fontWeight="bold"
                    className="pointer-events-none select-none"
                  >
                    {block.label.length > truncateLength ? block.label.substring(0, truncateLength - 2) + '...' : block.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoveredBlock && tooltipPosition && (
          <div
            className="absolute bg-black text-white text-xs px-3 py-2 rounded pointer-events-none z-[9999] shadow-lg border border-gray-600"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              maxWidth: '200px',
              whiteSpace: 'normal'
            }}
          >
            <div className="font-bold">{hoveredBlock}</div>
            <div className="text-[10px] mt-1">
              {(() => {
                const block = data.find(b => b.label === hoveredBlock);
                if (!block) return null;
                const area = (block.width * block.height * 100).toFixed(2);
                return `${area}% capi`;
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Treemap;

